import HeadingAndBodyLayout from './canvas-block-layouts/HeadingAndBodyLayout'
import HeadingAndSubLayout from './canvas-block-layouts/HeadingAndSubLayout'
import HeadingLayout from './canvas-block-layouts/HeadingLayout'
import ImageLayout from './canvas-block-layouts/ImageLayout'

import { generateBlockHash, generateBlockId } from './canvas-block-layouts/common'

export const ContentSlideLayouts = [
  HeadingLayout,
  HeadingAndSubLayout,
  HeadingAndBodyLayout,
  ImageLayout,
]

export { generateBlockId, generateBlockHash }

export const DefaultCanvasBlocks = HeadingAndBodyLayout.build

export function sanitizeBlocks(canvasBlocks) {
  Object.keys(canvasBlocks).forEach((blockId) => {
    if (blockId === 'key') {
      delete canvasBlocks[blockId]
    } else if (typeof (canvasBlocks[blockId]) === 'object') {
      canvasBlocks[blockId].id = blockId
      canvasBlocks[blockId].h = generateBlockHash()
    }

    // https://ahaslides.atlassian.net/browse/AHA-19070
    // Fix lỗi bị auto focus khi click lần đầu tiên vào text block
    if (canvasBlocks[blockId].type === 'RichText' &&
      canvasBlocks[blockId].content &&
      canvasBlocks[blockId].content.value) {
      canvasBlocks[blockId].content.value = canvasBlocks[blockId].content.value.replace('_mce_caret', '')
    }
  })
}

export function _migrateDefaultBlockIds(canvasBlocks, slideId) {
  if (!canvasBlocks) return null
  const newCanvasBlocks = { ...canvasBlocks }
  if (newCanvasBlocks['default-heading']) {
    newCanvasBlocks[`slide-${slideId}-default-heading`] = newCanvasBlocks['default-heading']
    newCanvasBlocks[`slide-${slideId}-default-heading`].tag = 'heading'
    delete newCanvasBlocks['default-heading']
  }

  if (newCanvasBlocks['default-content']) {
    newCanvasBlocks[`slide-${slideId}-default-content`] = newCanvasBlocks['default-content']
    newCanvasBlocks[`slide-${slideId}-default-content`].tag = 'content'
    delete newCanvasBlocks['default-content']
  }

  if (newCanvasBlocks[`slide-${slideId}-default-heading`]) {
    newCanvasBlocks[`slide-${slideId}-default-heading`].tag = 'heading'
  }

  if (newCanvasBlocks[`slide-${slideId}-default-content`]) {
    newCanvasBlocks[`slide-${slideId}-default-content`].tag = 'content'
  }

  sanitizeBlocks(newCanvasBlocks)

  return newCanvasBlocks
}

export function buildCanvasBlocksFromTemplate(canvasBlocks, slideId) {
  if (!canvasBlocks || !slideId) return null
  const newCanvasBlocks = JSON.parse(JSON.stringify(canvasBlocks))
  Object.keys(newCanvasBlocks).forEach((blockId) => {
    const newBlockId = generateBlockId(slideId)
    newCanvasBlocks[newBlockId] = newCanvasBlocks[blockId]
    newCanvasBlocks[newBlockId].id = newBlockId
    if (newCanvasBlocks[newBlockId].type === 'Image') {
      if (newCanvasBlocks[newBlockId].content.srcCache) {
        delete newCanvasBlocks[newBlockId].content.srcCache
      }
      if (newCanvasBlocks[newBlockId].content.blob) {
        delete newCanvasBlocks[newBlockId].content.blob
      }
    }
    delete newCanvasBlocks[blockId]
  })
  return newCanvasBlocks
}

export function createNewTextBlock(slideId) {
  if (!slideId) return null
  return {
    id: generateBlockId(slideId),
    type: 'RichText',
    tag: `text${(new Date()).getTime()}`,
    content: {
      value: '<p style="text-align: left;" data-mce-style="text-align: left;">Click to add text</p>',
    },
    style: {
      width: '600px',
      height: '60px',
      transform: 'translate(330px, 250px)',
    },
    h: generateBlockHash(),
  }
}

export function createEmojiBlock(slideId, emoji) {
  if (!slideId) return null
  return {
    id: generateBlockId(slideId),
    type: 'Emoji',
    content: {
      emoji,
    },
    style: {
      width: '120px',
      height: '120px',
      transform: 'translate(30px, 30px)',
    },
    h: generateBlockHash(),
  }
}

export function createShapeBlock(slideId, shape) {
  if (!slideId) return null

  return {
    id: generateBlockId(slideId),
    type: 'Shape',
    content: {
      shape,
      shapeStyle: {
        background: '#4af',
      },
    },
    style: {
      width: shape === 'star' ? `${120 * 1.05}px` : '120px',
      height: '120px',
      transform: 'translate(30px, 30px)',
    },
    h: generateBlockHash(),
  }
}

const LineProperties = {
  LINE_WEIGHT: 'lW',
  LINE_DASH: 'lD',
  LINE_START: 'lS',
  LINE_END: 'lE',
  LINE_COLOR: 'lC',
}

export function createLineBlock(slideId, type) {
  if (!slideId) return null
  const lineConfig = {
    [LineProperties.LINE_WEIGHT]: 3,
    [LineProperties.LINE_DASH]: null,
    [LineProperties.LINE_START]: null,
    [LineProperties.LINE_END]: null,
    [LineProperties.LINE_COLOR]: '#4af',
  }

  const arrowConfig = {
    [LineProperties.LINE_WEIGHT]: 3,
    [LineProperties.LINE_DASH]: null,
    [LineProperties.LINE_START]: null,
    [LineProperties.LINE_END]: 'arrow',
    [LineProperties.LINE_COLOR]: '#4af',
  }

  return {
    id: generateBlockId(slideId),
    h: generateBlockHash(),
    type: 'Line',
    content: type === 'arrow' ? arrowConfig : lineConfig,
    style: {
      width: '300px',
      height: '12px',
      minHeight: '12px',
      transform: 'translate(30px, 30px)',
    },
  }
}

export function calculateImageDimension(imageData) {
  const maxImageBlockWidth = 720
  const maxImageBlockHeight = 400
  const originalWidth = imageData.cropBoxData.width * 3
  const originalHeight = imageData.cropBoxData.height * 3
  const originalRatio = originalWidth / originalHeight
  const height = originalHeight > maxImageBlockHeight ? maxImageBlockHeight : originalHeight
  const width = originalWidth > maxImageBlockWidth ? maxImageBlockWidth : originalWidth

  if (originalHeight > originalWidth) {
    return {
      width: height * originalRatio,
      height,
    }
  }
  if (originalHeight < originalWidth) {
    return {
      width,
      height: width / originalRatio,
    }
  }
  return {
    width: maxImageBlockHeight,
    height: maxImageBlockHeight,
  }
}

export function calculateDefaultImageDimension(imageData) {
  const maxImageBlockHeight = 400
  const originalWidth = imageData.cropBoxData.width * 3
  const originalHeight = imageData.cropBoxData.height * 3
  const originalRatio = originalWidth / originalHeight
  const height = originalHeight > maxImageBlockHeight ? maxImageBlockHeight : originalHeight

  return {
    width: height * originalRatio,
    height,
  }
}

export function createNewImageBlock(slideId, imageUrl, imageCropperData) {
  if (!slideId) return null
  if (!imageCropperData.cropBoxData || !imageCropperData.cropBoxData.width || !imageCropperData.cropBoxData.height) {
    return null
  }
  const { width, height } = calculateImageDimension(imageCropperData)
  return {
    id: generateBlockId(slideId),
    type: 'Image',
    tag: 'image',
    content: {
      src: imageUrl,
    },
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: 'translate(20px, 0)',
    },
    h: generateBlockHash(),
  }
}
